import type { PostgrestError } from "@supabase/supabase-js";

/**
 * Erro para o caso em que um write (insert/update/delete) via Supabase/PostgREST
 * NÃO afetou nenhum registro.
 *
 * Por que isso importa: com RLS ligada e SEM policy de escrita, o PostgREST não
 * dá erro — ele casa 0 linhas e devolve 200/204. O supabase-js retorna
 * `error: null`, então um `if (error) throw error` NUNCA dispara e o app mostra
 * "sucesso" enquanto nada mudou (bug COR-349: "Completar" não ativava o projeto).
 * Também acontece quando o registro não existe / não é visível pelo usuário.
 *
 * Vai pro Sentry (via `shouldReport`): não tem `code` PGRST116/42xxx, então cai
 * no ramo `error instanceof Error` e é reportado como bug de verdade.
 */
export class SilentWriteError extends Error {
  readonly operation: string;

  constructor(operation: string) {
    super(
      `A operação "${operation}" não alterou nenhum registro. ` +
        "Isso costuma ser falta de permissão (RLS) ou registro inexistente. " +
        "Nada foi salvo.",
    );
    this.name = "SilentWriteError";
    this.operation = operation;
  }
}

/** Builder de escrita do supabase-js vira um thenable ao chamar `.select()`. */
interface SelectableWrite<Row> {
  select: (
    columns?: string,
  ) => PromiseLike<{ data: Row[] | null; error: PostgrestError | null }>;
}

/**
 * Executa um write do Supabase EXIGINDO que ele afete >= 1 registro.
 *
 * Força um `.select()` (senão o PostgREST não retorna as linhas afetadas e não
 * dá pra distinguir "0 linhas" de sucesso) e:
 *  - relança o `error` do PostgREST, se houver;
 *  - lança `SilentWriteError` se o write casou 0 linhas.
 *
 * Uso:
 *   await writeExpectingRows(
 *     supabase.from("controle_projetos").update({ status: "ativo" }).eq("id", id),
 *     "ativar projeto",
 *   );
 */
export async function writeExpectingRows<Row = Record<string, unknown>>(
  builder: SelectableWrite<Row>,
  operation: string,
  returning = "*",
): Promise<Row[]> {
  const { data, error } = await builder.select(returning);
  if (error) throw error;
  if (!data || data.length === 0) throw new SilentWriteError(operation);
  return data;
}
